import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import { GenericModal } from '../../../components/common/Modal/GenericModal';
import { GenericTable } from '../../../components/Sessions/GenericTable';
import DeleteIcon from '../../../../svg/DeleteIcon';
import EditIcon from '../../../../svg/EditIcon';
import KeyIcon from '../../../../svg/KeyIcon';
import { showErrorToast, showSuccessToast } from '../../../components/PluginsMenu/Toastr/Toastr';
import { ConfirmActionModal } from '../../../components/common/Modal/ConfirmActionModal';
import { Link } from 'react-router-dom';
import {
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useLazyGetAllRolesQuery,
  useUpdateRoleMutation,
} from '../../../../store/services/admin/RoleService';
import { RoleValidationSchema } from '../../../../validations';
import { Table } from '../../../components/Sessions/Table';
import CaretIcon from '../../../../svg/CaretIcon';

export const AdminRoleManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalButton, setModalButton] = useState('');
  const [currentRole, setCurrentRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();
  const [triggerGetAllRoles, { data, isLoading }] = useLazyGetAllRolesQuery();

  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const formikRef = useRef();

  useEffect(() => {
    triggerGetAllRoles({ PageNumber: currentPage, Take: pageSize, search: searchTerm });
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (data?.data) {
      setRoles(data.data.roles);
      setTotalCount(data.data.totalCount);
    }
  }, [data]);

  const handleAddRole = () => {
    setModalTitle('ADD ROLE');
    setModalButton('Add Role');
    setCurrentRole(null);
    setShowModal(true);
  };

  const handleEditClick = (id) => {
    const role = roles.find((r) => r.id === id);
    if (role) {
      setModalTitle('EDIT ROLE');
      setModalButton('Edit Role');
      setCurrentRole(role);
      setShowModal(true);
    }
  };

  const handleDeleteClick = (id) => {
    const role = roles.find((r) => r.id === id);
    if (role) {
      setRoleToDelete(role);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    const response = await deleteRole({ Id: roleToDelete.id });
    if (response?.data?.succeeded) {
      setRoles((prev) => prev.filter((role) => role.id !== roleToDelete.id));
      showSuccessToast('Role deleted successfully');
      setShowDeleteModal(false);
    } else {
      showErrorToast(response?.error?.data?.errors?.[0]);
    }
    setRoleToDelete(null);
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    const { roleName } = values;

    if (currentRole) {
      const response = await updateRole({ NewName: roleName, Id: currentRole.id });
      if (response?.data?.succeeded) {
        setRoles((prev) =>
          prev.map((r) => (r.id === currentRole.id ? { ...r, roleName } : r))
        );
        showSuccessToast('Role updated successfully');
      } else {
        showErrorToast(response?.error?.data?.errors?.[0]);
      }
    } else {
      const response = await createRole({ RoleName: roleName });
      if (response?.data?.succeeded) {
        const newId = response.data.data.id;
        setRoles((prev) => [...prev, { id: newId, roleName, userCount: 0 }]);
        showSuccessToast('New role added successfully');
      } else {
        showErrorToast(response?.error?.data?.errors?.[0]);
      }
    }

    setSubmitting(false);
    setShowModal(false);
  };

  const columns = [
    { header: 'Name', accessor: 'roleName' },
    {
      header: 'Member Count',
      accessor: 'userCount',
      render: (item) => (
        <div
          className={`bg-${item.userCount > 0 ? 'warning' : 'secondary'} text-center text-white rounded-pill py-1 px-3`}
          style={{ width: '60px', display: 'inline-block' }}
        >
          {item.userCount}
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (item) => (
        <div className="d-flex gap-2">
          <Link to={`role-permissions/${item.id}`}>
            <KeyIcon width={18} height={18} cursor="pointer" />
          </Link>
          <div onClick={(e) => { e.stopPropagation(); handleEditClick(item.id); }}>
            <EditIcon width={18} height={18} cursor="pointer" />
          </div>
          <div onClick={(e) => { e.stopPropagation(); handleDeleteClick(item.id); }}>
            <DeleteIcon fill="#ff0000" width={18} height={18} cursor="pointer" />
          </div>
        </div>
      ),
    },
  ];

  const buttonConfig = [
    { label: 'ADD ROLE', variant: 'primary', onClick: handleAddRole },
  ];

  if (isLoading) return <p>Loading Roles...</p>;

  const lastIndex = currentPage * pageSize;
  const firstIndex = lastIndex - pageSize;
  const totalPages = Math.ceil(totalCount / pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const visiblePages = pageNumbers.slice(
    Math.max(currentPage - 2, 0),
    Math.min(currentPage + 1, totalPages)
  );
  return (
    <div className="bg-white">
      <div className="col-xl-12">
        <div className="page-title flex-wrap mb-0">
          <div className="input-group search-area mb-md-0 ml-3">
            <input
              type="text"
              className="form-control ps-4"
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchTerm(e.target.value);
              }}
            />
          </div>
          <div>
            {buttonConfig.map(({ label, onClick, variant }, i) => (
              <button key={i} className={`btn btn-${variant}`} onClick={onClick}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Table data={roles} columns={columns} />

      <div id="example-student_wrapper" className="dataTables_wrapper no-footer">
        <div className="bg-white rounded shadow d-sm-flex text-center justify-content-between align-items-center mt-3">
          <div className="dataTables_info">
            Showing {firstIndex + 1} to {Math.min(lastIndex, totalCount)} of {totalCount} entries
          </div>
          <div className="dataTables_paginate paging_simple_numbers mb-0 me-2">
            <Link
              className={`previous ${currentPage === 1 ? 'disabled' : ''}`}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              <CaretIcon direction="right" width="25" height="25" />
            </Link>
            <span>
              {visiblePages.map((page) => (
                <Link
                  key={page}
                  className={`paginate_button ${currentPage === page ? 'current' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Link>
              ))}
            </span>
            <Link
              className={`next ${currentPage === totalPages ? 'disabled' : ''}`}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <CaretIcon direction="left" width="25" height="25" />
            </Link>
          </div>
        </div>
      </div>

      <Formik
        innerRef={formikRef}
        initialValues={{ roleName: currentRole?.roleName || '' }}
        validationSchema={RoleValidationSchema(!!currentRole, currentRole?.roleName)}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {({ handleSubmit, isSubmitting }) => (
          <GenericModal
            show={showModal}
            handleClose={() => setShowModal(false)}
            title={modalTitle}
            handleSave={() => formikRef.current?.submitForm()}
            createConfig={modalButton}
          >
            <FormikForm onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Field
                  as={Form.Control}
                  name="roleName"
                  type="text"
                  placeholder="Enter role title"
                />
                <div className="text-danger mt-1">
                  <ErrorMessage name="roleName" />
                </div>
              </Form.Group>
            </FormikForm>
          </GenericModal>
        )}
      </Formik>

      <ConfirmActionModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the role "${roleToDelete?.roleName}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};